import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { format, isToday, isTomorrow, parseISO, addDays, startOfWeek, endOfWeek } from 'date-fns';
import AddAgendaForm from '../components/AddAgendaForm';
import { getAgendas, createAgenda, updateAgenda, updateAgendaStatus, deleteAgenda } from '../services/api';
import { formatTime } from '../utils/timeFormat';
import { TEAM_COLORS, FULL_DAY_TEAMS, AGENDA_TEAMS, ATTENDING_TEAMS } from '../utils/constants';

const AgendaList = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [agendas, setAgendas] = useState([]);
  const [filteredAgendas, setFilteredAgendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('week'); // Default to week
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAgenda, setEditingAgenda] = useState(null);



  // Fetch agendas from API
  useEffect(() => {
    const fetchAgendas = async () => {
      try {
        setLoading(true);
        const fetchedAgendas = await getAgendas();

        // Convert date strings to Date objects and add canEdit property
        const processedAgendas = fetchedAgendas.map(agenda => ({
          ...agenda,
          date: new Date(agenda.date),
          endTime: agenda.endTime || null,
          canEdit: user?.isAdmin || agenda.userId === user?.id
        }));

        setAgendas(processedAgendas);
        setFilteredAgendas(processedAgendas);
      } catch (error) {
        console.error('Error fetching agendas:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAgendas();
  }, [user, isAdmin]);

  useEffect(() => {
    let filtered = agendas;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(agenda =>
        agenda.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agenda.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agenda.createdBy.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(agenda => agenda.status === statusFilter);
    } else {
      // If statusFilter is 'all', include pending agendas for all users
      filtered = filtered.filter(agenda => {
        if (agenda.status === 'pending') {
          // Show pending agendas only if user is admin or owner
          return agenda.canEdit;
        }
        return true;
      });
    }

    // Date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      switch (dateFilter) {
        case 'today':
          filtered = filtered.filter(agenda => isToday(new Date(agenda.date)));
          break;
        case 'tomorrow':
          filtered = filtered.filter(agenda => isTomorrow(new Date(agenda.date)));
          break;
        case 'week':
          // Indonesian week starts on Monday, ends on Sunday
          const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 });
          const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 1 });
          filtered = filtered.filter(agenda => {
            const agendaDate = new Date(agenda.date);
            return agendaDate >= startOfCurrentWeek && agendaDate <= endOfCurrentWeek;
          });
          break;
        case 'next_week':
          // Get start and end of next week
          const nextWeekDate = addDays(today, 7);
          const startOfNextWeek = startOfWeek(nextWeekDate, { weekStartsOn: 1 });
          const endOfNextWeek = endOfWeek(nextWeekDate, { weekStartsOn: 1 });
          filtered = filtered.filter(agenda => {
            const agendaDate = new Date(agenda.date);
            return agendaDate >= startOfNextWeek && agendaDate <= endOfNextWeek;
          });
          break;
        case 'custom':
          if (customDateRange.start && customDateRange.end) {
            const startDate = new Date(customDateRange.start);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(customDateRange.end);
            endDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(agenda => {
              const agendaDate = new Date(agenda.date);
              return agendaDate >= startDate && agendaDate <= endDate;
            });
          }
          break;
        case 'past':
          const startOfPastToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          filtered = filtered.filter(agenda => new Date(agenda.date) < startOfPastToday);
          break;
        default:
          break;
      }
    }

    setFilteredAgendas(filtered);
  }, [agendas, searchTerm, statusFilter, dateFilter, customDateRange]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getDateLabel = (date) => {
    if (isToday(date)) return 'Hari Ini';
    if (isTomorrow(date)) return 'Besok';
    return format(date, 'MMM dd, yyyy');
  };

  const handleApprove = async (agendaId) => {
    try {
      const updatedAgenda = await updateAgendaStatus(agendaId, 'approved');
      setAgendas(prev => prev.map(agenda =>
        agenda.id === agendaId ? { ...updatedAgenda, date: new Date(updatedAgenda.date) } : agenda
      ));
    } catch (error) {
      console.error('Error approving agenda:', error);
      // TODO: Add error notification
    }
  };

  const handleReject = async (agendaId) => {
    try {
      const updatedAgenda = await updateAgendaStatus(agendaId, 'rejected');
      setAgendas(prev => prev.map(agenda =>
        agenda.id === agendaId ? { ...updatedAgenda, date: new Date(updatedAgenda.date) } : agenda
      ));
    } catch (error) {
      console.error('Error rejecting agenda:', error);
      // TODO: Add error notification
    }
  };

  const handleDelete = async (agendaId) => {
    if (window.confirm('Are you sure you want to delete this agenda?')) {
      try {
        await deleteAgenda(agendaId);
        setAgendas(prev => prev.filter(agenda => agenda.id !== agendaId));
      } catch (error) {
        console.error('Error deleting agenda:', error);
        // TODO: Add error notification
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-light text-slate-800 tracking-tight">List Agenda</h1>
          <p className="mt-1 text-sm text-slate-500 font-medium">
            Tabel seluruh agenda lengkap
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => {
              if (user) {
                setShowAddModal(true);
              } else {
                navigate('/login');
              }
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 transition-all duration-300 shadow-slate-800/20 hover:scale-[1.02]"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Tambah Agenda Baru
          </button>
        </div>
      </div>

      {/* Edit Agenda Modal */}
      {showEditModal && editingAgenda && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg glass-panel flex flex-col max-h-[90vh]">
            <div className="p-8 overflow-y-auto no-scrollbar">
              <h3 className="text-2xl font-light text-slate-800 mb-8 tracking-tight shrink-0">Edit Agenda</h3>
              <AddAgendaForm
                key={editingAgenda.id}
                initialData={editingAgenda}
                onCancel={() => {
                  setShowEditModal(false);
                  setEditingAgenda(null);
                }}
                onSubmit={async (formData) => {
                  try {
                    console.log('onSubmit formData:', formData);
                    // Append seconds to endTime if missing
                    let updatedFormData = { ...formData };
                    if (updatedFormData.endTime && updatedFormData.endTime.length === 5) {
                      updatedFormData.endTime = updatedFormData.endTime + ':00';
                    }
                    // Call API to update agenda
                    const updatedAgenda = await updateAgenda(editingAgenda.id, updatedFormData);
                    updatedAgenda.date = new Date(updatedAgenda.date);
                    updatedAgenda.canEdit = user?.isAdmin || updatedAgenda.userId === user?.id;
                    setAgendas(prev => prev.map(agenda => agenda.id === updatedAgenda.id ? updatedAgenda : agenda));
                    setShowEditModal(false);
                    setEditingAgenda(null);
                  } catch (error) {
                    alert(error.message || 'Error updating agenda');
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="glass-panel p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Search agendas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-slate-200/60 rounded-xl leading-5 bg-white/50 backdrop-blur-sm placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-slate-200/60 rounded-xl leading-5 bg-white/50 backdrop-blur-sm text-slate-700 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 transition-all duration-300"
            >
              <option value="all">Semua Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Disetujui</option>
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-slate-200/60 rounded-xl leading-5 bg-white/50 backdrop-blur-sm text-slate-700 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 transition-all duration-300"
            >
              <option value="all">Semua Tanggal</option>
              <option value="today">Hari Ini</option>
              <option value="tomorrow">Besok</option>
              <option value="week">Minggu Ini</option>
              <option value="next_week">Minggu Depan</option>
              <option value="past">Sebelumnya</option>
              <option value="custom">Pilih Tanggal (Manual)</option>
            </select>
          </div>
        </div>

        {/* Custom Date Inputs (Conditional) */}
        {dateFilter === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 animate-fade-in">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mulai Tanggal</label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="block w-full px-3 py-2 border border-slate-200/60 rounded-xl leading-5 bg-white/50 backdrop-blur-sm text-slate-700 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 transition-all duration-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sampai Tanggal</label>
              <input
                type="date"
                min={customDateRange.start}
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="block w-full px-3 py-2 border border-slate-200/60 rounded-xl leading-5 bg-white/50 backdrop-blur-sm text-slate-700 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 transition-all duration-300"
              />
            </div>
          </div>
        )}
      </div>

      {/* Agenda List */}
      <div className="glass-panel overflow-hidden">
        {filteredAgendas.length === 0 ? (
          <div className="text-center py-12">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Tidak ada agenda ditemukan</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || dateFilter !== 'all'
                ? 'Coba ubah filter untuk melihat hasil lainnya.'
                : 'Mulai buat agenda baru.'}
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  if (user) {
                    setShowAddModal(true);
                  } else {
                    navigate('/login');
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-slate-800 hover:bg-slate-700 transition-all duration-300"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Tambah Agenda Baru
              </button>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-200/50">
            {filteredAgendas.map((agenda) => {
              const teamColor = TEAM_COLORS[agenda.team] || TEAM_COLORS.default;
              const isFullDay = FULL_DAY_TEAMS.includes(agenda.team);

              return (
                <li key={agenda.id} className={`p-6 group transition-colors duration-300 ${isFullDay ? 'bg-amber-50/40 hover:bg-amber-50/70 border-amber-100/50' : 'hover:bg-white/40'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center space-x-3 mb-1">
                        <div className={`shrink-0 w-2 h-2 rounded-full ring-2 ring-offset-2 ${teamColor.split(' ')[0]} ${teamColor.replace('bg-', 'ring-')}`}></div>
                        <h3 className="text-xl font-medium text-slate-800 tracking-tight">
                          {agenda.title}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium tracking-wide uppercase ${getStatusColor(agenda.status)}`}>
                          {agenda.status}
                        </span>
                      </div>
                      <p className="mt-1.5 text-[15px] text-slate-500 leading-relaxed ml-5">
                        {agenda.description}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-y-2 items-center text-sm text-slate-500 ml-5">
                        <div className="flex items-center mr-6">
                          <CalendarIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                          <span className="font-medium text-slate-600">{getDateLabel(agenda.date)}</span>
                        </div>
                        <div className="flex items-center mr-6">
                          <ClockIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                          <span className="font-medium text-slate-600">{formatTime(agenda.time)} - {formatTime(agenda.endTime)}</span>
                        </div>
                        <div className="flex items-center space-x-6">
                          <div className="flex items-center">
                            <UserIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                            {AGENDA_TEAMS[agenda.team] || agenda.createdBy}
                          </div>
                          <div className="flex items-center">
                            <UserIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                            <span className="text-slate-400">Dihadiri oleh&nbsp;</span>
                            {ATTENDING_TEAMS[agenda.attendingTeam] || 'Tidak diketahui'}
                          </div>
                          {agenda.location && (
                            <div className="flex items-center">
                              <MapPinIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                              <span className="text-slate-600">{agenda.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Admin Actions */}
                      {isAdmin && agenda.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(agenda.id)}
                            className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            title="Approve"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleReject(agenda.id)}
                            className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            title="Reject"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}

                      {/* Edit/Delete Actions */}
                      {agenda.canEdit && (
                        <>
                          <button
                            onClick={() => {
                              // Open edit modal with selected agenda data
                              setEditingAgenda(agenda);
                              setShowEditModal(true);
                            }}
                            className="inline-flex items-center p-2 rounded-full text-slate-400 hover:text-primary-600 hover:bg-primary-50 focus:outline-none transition-all duration-200"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(agenda.id)}
                            className="inline-flex items-center p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 focus:outline-none transition-all duration-200"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Add Agenda Modal */}
      {user && showAddModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg glass-panel flex flex-col max-h-[90vh]">
            <div className="p-8 overflow-y-auto no-scrollbar">
              <h3 className="text-2xl font-light text-slate-800 mb-8 tracking-tight shrink-0">Tambah Agenda Baru</h3>
              <AddAgendaForm
                onCancel={() => setShowAddModal(false)}
                onSubmit={async (formData) => {
                  try {
                    // Add createdBy field as selected team, remove status to let server set it
                    const newAgenda = {
                      ...formData,
                      createdBy: user?.name || 'Unknown',
                      createdAt: new Date().toISOString()
                    };
                    // Call API to create agenda
                    const createdAgenda = await createAgenda(newAgenda);
                    // Convert date string to Date object
                    createdAgenda.date = new Date(createdAgenda.date);
                    // Update agendas state
                    setAgendas(prev => [...prev, createdAgenda]);
                    setShowAddModal(false);
                  } catch (error) {
                    alert(error.message || 'Error adding agenda');
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AgendaList;
